import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const WelcomeRedirect = () => {
  const navigate = useNavigate();
  const [productName, setProductName] = useState('your fit');

  useEffect(() => {
    let pending = null;
    try {
      const raw = sessionStorage.getItem('pendingProduct');
      if (raw) pending = JSON.parse(raw);
    } catch (e) {
      pending = null;
    }

    if (pending?.product_name) {
      setProductName(pending.product_name);
    }

    // Track GA event for the originally intended click
    if (pending && window.gtag) {
      window.gtag('event', 'post_login_product_redirect', {
        product_name: pending.product_name,
        product_link: pending.product_link,
        event_category: 'Product Engagement',
        event_label: pending.product_name,
      });
    }

    const timer = setTimeout(() => {
      sessionStorage.removeItem('pendingProduct');
      if (pending?.product_link) {
        // Open product in same tab so the redirect feels seamless
        window.location.href = pending.product_link;
      } else {
        navigate('/', { replace: true });
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-[#F9F8F6] welcome-redirect-fade"
      data-testid="welcome-redirect-page"
    >
      <div className="max-w-xl w-full text-center">
        {/* Animated rings */}
        <div className="relative w-24 h-24 mx-auto mb-8" data-testid="welcome-redirect-animation">
          <span className="absolute inset-0 rounded-full border border-[#2D302A]/20 welcome-ring welcome-ring-1"></span>
          <span className="absolute inset-2 rounded-full border border-[#2D302A]/30 welcome-ring welcome-ring-2"></span>
          <span className="absolute inset-4 rounded-full border border-[#2D302A]/40 welcome-ring welcome-ring-3"></span>
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-3 h-3 rounded-full bg-[#2D302A] welcome-dot"></span>
          </span>
        </div>

        <p
          className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#6B6A68] mb-4"
          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          data-testid="welcome-redirect-eyebrow"
        >
          Taking you to your fit
        </p>

        <h1
          className="text-4xl md:text-5xl tracking-tighter text-[#2D302A] mb-6 line-clamp-2"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
          data-testid="welcome-redirect-title"
        >
          {productName}
        </h1>

        <p
          className="text-sm md:text-base text-[#6B6A68] leading-relaxed mb-3"
          data-testid="welcome-redirect-message"
        >
          Welcome to North of Six Club - a community built by tall people, for tall people.
        </p>

        <p
          className="text-sm md:text-base text-[#6B6A68] leading-relaxed"
          data-testid="welcome-redirect-cta"
        >
          Found something that works? <span className="text-[#2D302A]">Share it back</span>, and tell another tall friend about us.
        </p>

        {/* Loading bar */}
        <div className="mt-10 h-px w-full bg-[#E4E1D9] overflow-hidden rounded-full" data-testid="welcome-redirect-progress">
          <div className="h-full bg-[#2D302A] welcome-progress"></div>
        </div>
      </div>

      <style>{`
        @keyframes welcomeFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .welcome-redirect-fade > div {
          animation: welcomeFadeIn 0.6s ease-out both;
        }

        @keyframes welcomeRingPulse {
          0% { transform: scale(0.85); opacity: 0.9; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        .welcome-ring {
          animation: welcomeRingPulse 1.8s ease-out infinite;
        }
        .welcome-ring-2 { animation-delay: 0.3s; }
        .welcome-ring-3 { animation-delay: 0.6s; }

        @keyframes welcomeDotBeat {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.25); opacity: 0.7; }
        }
        .welcome-dot {
          animation: welcomeDotBeat 1.4s ease-in-out infinite;
        }

        @keyframes welcomeProgressFill {
          from { width: 0%; }
          to { width: 100%; }
        }
        .welcome-progress {
          width: 0%;
          animation: welcomeProgressFill 2s linear forwards;
        }
      `}</style>
    </div>
  );
};

export default WelcomeRedirect;
